import React, { useState, useEffect } from "react";
import { Nav, Accordion, Button } from "react-bootstrap";
import { Printer } from "react-bootstrap-icons";
import "./drawer.css";
import Circle from "../Circle";

const Drawer = ({
  hasProfileImage,
  title,
  metaDataContent,
  issueContent,
  closeDrawer,
  activeSection,
  foundSubsection,
  foundSection,
  disclaimer,
}) => {
  // Initializing all top-level sections to be closed by default
  const [sectionActiveKeys, setSectionActiveKeys] = useState([]);

  // Function to toggle an accordion section
  const toggleAccordion = (key) => {
    setSectionActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    if (foundSection) {
      setSectionActiveKeys((prev) => {
        const sectionKey = `accordion-${foundSection?.name}`;
        return prev.includes(sectionKey) ? prev : [...prev, sectionKey];
      });
    }
  }, [foundSection]);

  /* UI components */
  const metaDataNavItems = Object.keys(metaDataContent).map((item, index) => {
    if (!["Project Information", "Project Overview"].includes(item)) {
      return null;
    }

    return (
      <Nav key={item} className="metadata-nav-item">
        <div
          id={`nav-item-${item.toLowerCase()}`}
          className={`mx-2 w-100 ${
            activeSection === item.toLowerCase() ? "nav-item-active" : ""
          }`}
        >
          <Nav.Link href={`#${item.toLowerCase()}`} onClick={closeDrawer}>
            <div className="d-flex justify-content-between w-100">{item}</div>
          </Nav.Link>
        </div>
        <hr className="w-100 m-2" />
      </Nav>
    );
  });

  const issueContentArray = issueContent.map((section, index) => {
    const hasSubsections =
      section.subsections && section.subsections.length > 0;

    const sectionId = `section-${section.name?.toLowerCase().replace(/\s+/g, "-")}`;
    const isSectionActive = activeSection === sectionId;

    return (
      <Accordion.Item eventKey={`accordion-${section.name}`} key={section.name}>
        <Accordion.Header
          className={`section-accordion-header ${isSectionActive ? "active" : ""}`}
          onClick={() => toggleAccordion(`accordion-${section.name}`)}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>{section.name}</div>
          </div>
        </Accordion.Header>
        {hasSubsections && (
          <Accordion.Body>
            {section.subsections.map((subsection) => {
              const subsectionId = `subsection-${subsection.name?.toLowerCase().replace(/\s+/g, "-")}`;
              const isSubsectionActive = activeSection === subsectionId;

              // Count total issues in this subsection
              const issueCount =
                (subsection?.issues?.length || 0) +
                (subsection?.infos?.length || 0) +
                (subsection?.limits?.length || 0);

              return (
                <div
                  key={subsection.name}
                  className={`drawer-nav-item subsection-nav-item ${isSubsectionActive ? "active" : ""}`}
                >
                  <Nav.Link href={`#${subsectionId}`} onClick={closeDrawer}>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <span>{subsection.name}</span>
                      {issueCount > 0 && (
                        <Circle type="general" text={issueCount} />
                      )}
                    </div>
                  </Nav.Link>
                </div>
              );
            })}
          </Accordion.Body>
        )}
      </Accordion.Item>
    );
  });

  return (
    <div className="drawer">
      {/* Fixed header with logo and company name */}
      <div className="drawer-header">
        <div className="drawer-header-logo-container">
          {hasProfileImage ? (
            <img
              src={hasProfileImage}
              alt={title || "Company logo"}
              className="drawer-header-logo"
            />
          ) : (
            <div className="drawer-header-logo-placeholder" />
          )}
        </div>
        {title && <div className="drawer-header-company-name">{title}</div>}
      </div>

      <div className="drawer-content-container">
        <div className="sidebar-section-title">REPORT & INTRODUCTION</div>
        {disclaimer && (
          <div
            id="nav-item-inspection-scope-disclaimer"
            className={`drawer-nav-item ${activeSection === "inspection-scope-disclaimer" ? "active" : ""}`}
          >
            <Nav.Link href="#inspection-scope-disclaimer" onClick={closeDrawer}>
              Inspection Scope & Disclaimer
            </Nav.Link>
          </div>
        )}
        <div
          id="nav-item-definitions-glossary"
          className={`drawer-nav-item ${activeSection === "definitions-glossary" ? "active" : ""}`}
        >
          <Nav.Link href="#definitions-glossary" onClick={closeDrawer}>
            Definitions & Glossary
          </Nav.Link>
        </div>
        <hr className="drawer-divider" />

        <div className="sidebar-section-title">OVERVIEW & SUMMARY</div>
        <div
          id="nav-item-inspection-overview"
          className={`drawer-nav-item ${activeSection === "inspection-overview" ? "active" : ""}`}
        >
          <Nav.Link href="#inspection-overview" onClick={closeDrawer}>
            Inspection Overview
          </Nav.Link>
        </div>
        <div
          id="nav-item-challenge-summary"
          className={`drawer-nav-item ${activeSection === "challenge-summary-section" ? "active" : ""}`}
        >
          <Nav.Link href="#challenge-summary-section" onClick={closeDrawer}>
            Challenge Summary
          </Nav.Link>
        </div>
        <hr className="drawer-divider" />

        <div className="sidebar-section-title">INSPECTION</div>
        <div
          id="nav-item-property-details"
          className={`drawer-nav-item ${activeSection === "property-details" ? "active" : ""}`}
        >
          <Nav.Link href="#property-details" onClick={closeDrawer}>
            Property Details
          </Nav.Link>
        </div>
        <Accordion
          className="section-accordion-container"
          activeKey={sectionActiveKeys}
        >
          {issueContentArray}
        </Accordion>
      </div>

      {/* Fixed footer with Print Report button */}
      <div className="drawer-footer">
        <Button
          variant="outline-primary"
          className="print-report-button"
          onClick={() => window.print()}
        >
          <Printer size={18} />
          Print Report
        </Button>
      </div>
    </div>
  );
};

export default Drawer;
